// src/contexts/PaymentContext/PaymentContext.jsx
// 💳 Contexto de Pagamentos - Gerencia planos, assinaturas e trial de 7 dias
// Versão: 3.0 | Trial System inspirado em Netflix, Spotify, Notion, Vercel
// 🔥 V3: Trial de 7 dias automático, bloqueio ao expirar, modal obrigatório

import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { features } from '../../config/features';
import { useAuth } from '../useAuth';

// ============================================
// 🔑 CHAVES DO LOCALSTORAGE
// ============================================
const TRIAL_KEY = 'propzyn_trial';

// ============================================
// 🎯 HELPERS DE TRIAL
// ============================================
const getTrialData = () => {
  try {
    const data = localStorage.getItem(TRIAL_KEY);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

const saveTrialData = (data) => {
  localStorage.setItem(TRIAL_KEY, JSON.stringify(data));
};

const clearTrialData = () => {
  localStorage.removeItem(TRIAL_KEY);
};

// ============================================
// 🎯 ESTADO INICIAL
// ============================================
const initialState = {
  // Plano atual do usuário
  currentPlan: null,
  planStatus: null,
  planExpiresAt: null,
  
  // 🔥 V3: Trial
  trialStart: null,
  trialEnd: null,
  trialPlan: null,
  isTrialActive: false,
  isTrialExpired: false,
  trialDaysRemaining: 0,
  
  // Histórico de pagamentos
  payments: [],
  subscriptions: [],
  
  // UI States
  loading: false,
  error: null,
  showUpgradeModal: false,
  upgradeModalDismissable: true, // 🔥 V3: false quando trial expirou
  
  // Feature flags
  isEnabled: features.payments.enabled,
  gateway: features.payments.gateway,
  environment: features.payments.environment,
  
  // Planos disponíveis
  availablePlans: Object.values(features.payments.plans || {})
};

// ============================================
// 🎯 ACTION TYPES
// ============================================
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_USER_PLAN: 'SET_USER_PLAN',
  SET_PAYMENTS: 'SET_PAYMENTS',
  SET_SUBSCRIPTIONS: 'SET_SUBSCRIPTIONS',
  TOGGLE_UPGRADE_MODAL: 'TOGGLE_UPGRADE_MODAL',
  CLEAR_ERROR: 'CLEAR_ERROR',
  RESET_STATE: 'RESET_STATE',
  // 🔥 V3: Trial actions
  SET_TRIAL: 'SET_TRIAL',
  CLEAR_TRIAL: 'CLEAR_TRIAL'
};

// ============================================
// 🎯 REDUCER
// ============================================
const paymentReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
      
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
      
    case ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };
      
    case ACTIONS.SET_USER_PLAN:
      return {
        ...state,
        currentPlan: action.payload.plan,
        planStatus: action.payload.status,
        planExpiresAt: action.payload.expiresAt,
        loading: false
      };
      
    case ACTIONS.SET_PAYMENTS:
      return { ...state, payments: action.payload };
      
    case ACTIONS.SET_SUBSCRIPTIONS:
      return { ...state, subscriptions: action.payload };
      
    case ACTIONS.TOGGLE_UPGRADE_MODAL:
      return { 
        ...state, 
        showUpgradeModal: action.payload.show,
        upgradeModalDismissable: action.payload.dismissable !== false
      };
      
    // 🔥 V3: Trial
    case ACTIONS.SET_TRIAL:
      return {
        ...state,
        trialStart: action.payload.trialStart,
        trialEnd: action.payload.trialEnd,
        trialPlan: action.payload.trialPlan,
        isTrialActive: action.payload.isTrialActive,
        isTrialExpired: action.payload.isTrialExpired,
        trialDaysRemaining: action.payload.trialDaysRemaining,
        currentPlan: action.payload.trialPlan || state.currentPlan,
        planStatus: action.payload.isTrialExpired ? 'expired' : 'trial'
      };
      
    case ACTIONS.CLEAR_TRIAL:
      clearTrialData();
      return {
        ...state,
        trialStart: null,
        trialEnd: null,
        trialPlan: null,
        isTrialActive: false,
        isTrialExpired: false,
        trialDaysRemaining: 0
      };
      
    case ACTIONS.RESET_STATE:
      return { ...initialState, isEnabled: state.isEnabled };
      
    default:
      return state;
  }
};

// ============================================
// 🎯 CRIAÇÃO DO CONTEXTO
// ============================================
const PaymentContext = createContext();

// ============================================
// 🎯 HOOK PERSONALIZADO PARA USAR O CONTEXTO
// ============================================
export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment deve ser usado dentro de PaymentProvider');
  }
  return context;
};

// ============================================
// 🎯 PROVIDER
// ============================================
export const PaymentProvider = ({ children }) => {
  const [state, dispatch] = useReducer(paymentReducer, initialState);
  const { user } = useAuth();

  // ============================================
  // 🔥 V3: VERIFICAR TRIAL AO INICIAR
  // ============================================
  const checkTrialStatus = useCallback(() => {
    const trialData = getTrialData();
    
    if (!trialData) {
      return { isActive: false, isExpired: false, daysRemaining: 0 };
    }
    
    const now = new Date();
    const trialEnd = new Date(trialData.trialEnd);
    const isExpired = now > trialEnd;
    const isActive = !isExpired;
    
    // Calcular dias restantes
    const diffTime = trialEnd.getTime() - now.getTime();
    const daysRemaining = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
    
    return {
      isActive,
      isExpired,
      daysRemaining,
      trialStart: trialData.trialStart,
      trialEnd: trialData.trialEnd,
      trialPlan: trialData.trialPlan
    };
  }, []);

  // ============================================
  // 🔥 V3: INICIAR TRIAL DE 7 DIAS
  // ============================================
  const startTrial = useCallback((planId) => {
    const now = new Date();
    const trialEnd = new Date(now);
    trialEnd.setDate(trialEnd.getDate() + 7); // 7 dias de trial
    
    const trialData = {
      trialStart: now.toISOString(),
      trialEnd: trialEnd.toISOString(),
      trialPlan: planId
    };
    
    saveTrialData(trialData);
    
    dispatch({
      type: ACTIONS.SET_TRIAL,
      payload: {
        trialStart: now.toISOString(),
        trialEnd: trialEnd.toISOString(),
        trialPlan: planId,
        isTrialActive: true,
        isTrialExpired: false,
        trialDaysRemaining: 7
      }
    });
    
    console.log(`🎉 Trial iniciado! Plano: ${planId} · Vence em: ${trialEnd.toLocaleDateString('pt-BR')}`);
    return trialData;
  }, []);

  // ============================================
  // 🔥 V3: VERIFICAR SE TRIAL EXPIRED (bloqueio)
  // ============================================
  const checkTrialExpired = useCallback(() => {
    const { isExpired, daysRemaining, trialPlan } = checkTrialStatus();
    
    if (isExpired) {
      // Mostrar modal de upgrade NÃO dispensável
      dispatch({
        type: ACTIONS.TOGGLE_UPGRADE_MODAL,
        payload: { show: true, dismissable: false }
      });
      
      dispatch({
        type: ACTIONS.SET_TRIAL,
        payload: {
          trialStart: null,
          trialEnd: null,
          trialPlan: trialPlan,
          isTrialActive: false,
          isTrialExpired: true,
          trialDaysRemaining: 0
        }
      });
      
      return true;
    }
    
    return false;
  }, [checkTrialStatus]);

  // ============================================
  // 🔄 CARREGAR PLANO DO USUÁRIO
  // ============================================
  const loadUserPlan = useCallback(async () => {
    if (!user || !state.isEnabled) {
      dispatch({ type: ACTIONS.SET_USER_PLAN, payload: { plan: 'unlimited', status: 'active' } });
      return;
    }

    dispatch({ type: ACTIONS.SET_LOADING, payload: true });

    try {
      // 🔥 V3: Verificar trial primeiro
      const { isActive, isExpired, daysRemaining, trialStart, trialEnd, trialPlan } = checkTrialStatus();
      
      if (isActive) {
        dispatch({
          type: ACTIONS.SET_TRIAL,
          payload: {
            trialStart,
            trialEnd,
            trialPlan,
            isTrialActive: true,
            isTrialExpired: false,
            trialDaysRemaining: daysRemaining
          }
        });
        return;
      }
      
      if (isExpired) {
        dispatch({
          type: ACTIONS.SET_TRIAL,
          payload: {
            trialStart: null,
            trialEnd,
            trialPlan,
            isTrialActive: false,
            isTrialExpired: true,
            trialDaysRemaining: 0
          }
        });
        return;
      }

      // Buscar plano do usuário na database global
      const usersDb = JSON.parse(localStorage.getItem('imoveispro_users_db') || '{"users":[]}');
      const userData = usersDb.users.find(u => u.id === user.id);

      if (userData) {
        dispatch({
          type: ACTIONS.SET_USER_PLAN,
          payload: {
            plan: userData.plan || 'basic',
            status: userData.planStatus || 'active',
            expiresAt: userData.planExpiresAt
          }
        });
      } else {
        // Fallback para plano básico
        dispatch({
          type: ACTIONS.SET_USER_PLAN,
          payload: { plan: 'basic', status: 'active', expiresAt: null }
        });
      }
    } catch (error) {
      console.error('❌ Erro ao carregar plano:', error);
      dispatch({
        type: ACTIONS.SET_ERROR,
        payload: 'Erro ao carregar informações do plano'
      });
    }
  }, [user, state.isEnabled, checkTrialStatus]);

  // ============================================
  // 🔄 CARREGAR AO INICIAR
  // ============================================
  useEffect(() => {
    loadUserPlan();
  }, [loadUserPlan]);

  // ============================================
  // 🔥 V3: VERIFICAR EXPIRAÇÃO DO TRIAL AO MONTAR
  // ============================================
  useEffect(() => {
    checkTrialExpired();
  }, [checkTrialExpired]);

  // ============================================
  // 💰 FUNÇÕES DE PAGAMENTO
  // ============================================

  /**
   * Iniciar processo de checkout
   */
  const startCheckout = useCallback(async (planId, paymentMethod = 'pix') => {
    if (!state.isEnabled) {
      console.log('💳 Pagamentos desativados (modo unlimited)');
      return { success: true, mock: true };
    }

    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    dispatch({ type: ACTIONS.CLEAR_ERROR });

    try {
      // TODO: Integrar com backend real
      await new Promise(resolve => setTimeout(resolve, 2000));

      const mockResponse = {
        success: true,
        checkoutUrl: `https://checkout.mercadopago.com/${planId}`,
        paymentId: `payment_${Date.now()}`,
        planId
      };

      return mockResponse;

    } catch (error) {
      dispatch({
        type: ACTIONS.SET_ERROR,
        payload: error.message || 'Erro ao iniciar pagamento'
      });
      return { success: false, error: error.message };
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  }, [state.isEnabled]);

  /**
   * Verificar status de um pagamento
   */
  const checkPaymentStatus = useCallback(async (paymentId) => {
    if (!state.isEnabled) return { status: 'approved' };

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return {
        status: 'approved',
        paymentId,
        approvedAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Erro ao verificar pagamento:', error);
      return { status: 'pending' };
    }
  }, [state.isEnabled]);

  /**
   * Cancelar assinatura
   */
  const cancelSubscription = useCallback(async (subscriptionId) => {
    if (!state.isEnabled) return { success: true };

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      await loadUserPlan();
      return { success: true };
    } catch (error) {
      console.error('Erro ao cancelar:', error);
      return { success: false, error: error.message };
    }
  }, [state.isEnabled, loadUserPlan]);

  /**
   * Atualizar método de pagamento
   */
  const updatePaymentMethod = useCallback(async (paymentMethod) => {
    if (!state.isEnabled) return { success: true };

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      return { success: true };
    } catch (error) {
      console.error('Erro ao atualizar método:', error);
      return { success: false, error: error.message };
    }
  }, [state.isEnabled]);

  /**
   * Obter histórico de pagamentos
   */
  const getPaymentHistory = useCallback(async () => {
    if (!state.isEnabled) return [];

    try {
      const mockHistory = [
        {
          id: 'pay_1',
          date: '2026-03-01',
          amount: 47,
          plan: 'basic',
          status: 'approved'
        }
      ];
      
      dispatch({ type: ACTIONS.SET_PAYMENTS, payload: mockHistory });
      return mockHistory;
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
      return [];
    }
  }, [state.isEnabled]);

  // ============================================
  // 🎯 FUNÇÕES DE PLANO
  // ============================================

  /**
   * Verificar se pode criar recurso
   */
  const canCreate = useCallback((resourceType, currentCount) => {
    // 🔥 V3: Se trial expirou, não pode criar nada
    if (state.isTrialExpired) return false;
    
    if (!state.isEnabled || state.currentPlan === 'unlimited') return true;

    const limits = {
      basic: { imoveis: 10, clientes: 50 },
      professional: { imoveis: 50, clientes: 200 },
      enterprise: { imoveis: Infinity, clientes: Infinity }
    };

    const planLimits = limits[state.currentPlan] || limits.basic;
    const limit = planLimits[resourceType] || Infinity;

    return currentCount < limit;
  }, [state.isEnabled, state.currentPlan, state.isTrialExpired]);

  /**
   * Obter mensagem de limite
   */
  const getLimitMessage = useCallback((resourceType, currentCount) => {
    // 🔥 V3: Mensagem de trial expirado
    if (state.isTrialExpired) {
      return 'Seu período de teste gratuito expirou. Escolha um plano para continuar.';
    }
    
    if (!state.isEnabled) return null;

    const limits = {
      basic: { imoveis: 10, clientes: 50 },
      professional: { imoveis: 50, clientes: 200 },
      enterprise: { imoveis: Infinity, clientes: Infinity }
    };

    const planLimits = limits[state.currentPlan] || limits.basic;
    const limit = planLimits[resourceType];

    if (currentCount >= limit) {
      return `Limite de ${resourceType} atingido (${currentCount}/${limit}). Faça upgrade para continuar.`;
    }

    return null;
  }, [state.isEnabled, state.currentPlan, state.isTrialExpired]);

  /**
   * Abrir modal de upgrade
   */
  const openUpgradeModal = useCallback((dismissable = true) => {
    dispatch({ 
      type: ACTIONS.TOGGLE_UPGRADE_MODAL, 
      payload: { show: true, dismissable } 
    });
  }, []);

  /**
   * Fechar modal de upgrade
   */
  const closeUpgradeModal = useCallback(() => {
    // 🔥 V3: Se trial expirou, NÃO pode fechar o modal
    if (state.isTrialExpired && !state.upgradeModalDismissable) {
      return;
    }
    dispatch({ 
      type: ACTIONS.TOGGLE_UPGRADE_MODAL, 
      payload: { show: false, dismissable: true } 
    });
  }, [state.isTrialExpired, state.upgradeModalDismissable]);

  // ============================================
  // 🔥 V3: VALOR DO CONTEXTO (com trial)
  // ============================================
  const value = {
    ...state,
    
    // 🔥 V3: Trial
    startTrial,
    checkTrialExpired,
    getTrialStatus: checkTrialStatus,
    
    // Funções de pagamento
    startCheckout,
    checkPaymentStatus,
    cancelSubscription,
    updatePaymentMethod,
    getPaymentHistory,
    
    // Funções de plano
    canCreate,
    getLimitMessage,
    openUpgradeModal,
    closeUpgradeModal,
    
    // Recarregar plano
    refreshPlan: loadUserPlan
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};

export default PaymentContext;